import React from "react";
import { IAdmins, IUserDetails } from "../Interface";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Slide,
    Tab,
    Tabs,
    Tooltip,
    DialogActions,
    Button,
    Typography,
    Box
} from "@mui/material";
import { Close } from "@mui/icons-material";
import LoginHistory from "../LoginHistory/LoginHistory";
import StatusHistory from "../StatusHistory/StatusHistory";
import UserSettings from "../UserSettings/UserSettings";
import TabPanel from "../../../Component/TabPanel/TabPanel";
import { TransitionProps } from "@mui/material/transitions";
import { SyncContext, SyncContextType } from "../../../Contexts/BatchSyncContext";
import TransactionLogs from "../../TransactionLogs/TransactionLogs";
import UserCards from "../UserCards/UserCards"

// Interface for CommonUserDialog props
interface CommonUserDialogProps {
    open: boolean;
    onClose?: (state: boolean, action: string) => void;
    userId: string;
    accessToken: string;
    selectedUserData: IUserDetails;
    isStatusHistoryVisible: boolean
    selectedAdminData: IAdmins;
    selectedTab: number;
    isTransactionLogsVisible: boolean;
}

// Interface for CommonUserDialog state
interface CommonUserDialogState {
    tabValue: number;
    hasErrors: boolean;
    isLimitConfirm: boolean;
}

// Slide Transition for Dialog
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

enum SelectedTab {
    MOBILE_USERS = 0,
    SUPPORT_STAFFS = 1,
    ADMIN = 2
}

// Main class component
class CommonUserDialog extends React.Component<CommonUserDialogProps, CommonUserDialogState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private userSettingsRef = React.createRef<UserSettings>();
    constructor(props: CommonUserDialogProps) {
        super(props);
        this.state = {
            tabValue: 0,
            isLimitConfirm: false,
            hasErrors: false
        };
    }

    // Tab change handler
    handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        this.setState({ tabValue: newValue });
    };

    // Close the dialog
    handleClose = (action: string) => {
        this.props.onClose?.(false, action);
    };

    // On save button click, update the UserSettings state
    handleSave = () => {
        if (this.userSettingsRef.current) {
            console.log("Updating UserSettings state...");
            this.userSettingsRef.current.setState({ isLimitConfirm: true });
        }
    };

    // On save button click, check if there are any validation errors
    handleValidationChange = (hasErrors: boolean) => {
        console.log("Validation Status: ", hasErrors);
        this.setState({ hasErrors });
    };

    render() {
        const { tabValue, hasErrors } = this.state;
        const { open, userId, accessToken, selectedUserData, selectedAdminData } = this.props;
        const { userDetails } = this.context.state.permissions

        const name =
            this.props.selectedTab === SelectedTab.MOBILE_USERS
                ? selectedUserData?.name || ""
                : this.props.selectedTab === SelectedTab.ADMIN
                    ? selectedAdminData?.first_name || ""
                    : "";

        const email =
            this.props.selectedTab === SelectedTab.MOBILE_USERS
                ? selectedUserData?.email || ""
                : this.props.selectedTab === SelectedTab.ADMIN
                    ? selectedAdminData?.email || ""
                    : "";

        const mobile =
            this.props.selectedTab === SelectedTab.MOBILE_USERS
                ? selectedUserData?.mobile_number || ""
                : this.props.selectedTab === SelectedTab.ADMIN
                    ? selectedAdminData?.mobile_number || ""
                    : "";

        return (
            <Dialog
                TransitionComponent={Transition}
                open={open}
                onClose={() => this.handleClose("cancel")}
                fullWidth
                keepMounted
                maxWidth="lg"
                sx={{ zIndex: 10 }}
            >
                <DialogTitle sx={{ m: "12px 12px 0 12px", p: 2, position: "relative" }}>
                    <Tabs
                        value={tabValue}
                        onChange={this.handleTabChange}
                        aria-label="settings tabs"
                        indicatorColor="secondary"
                        textColor="primary"
                        sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                        <Tab label="User Settings" />
                        {this.props.isStatusHistoryVisible && <Tab label="Status History" />}
                        <Tab label="Login History" />
                        {this.props.isTransactionLogsVisible && <Tab label="Transaction Log" />}
                        <Tab label="User Cards" />
                    </Tabs>
                    <Tooltip title="Close">
                        <IconButton
                            aria-label="close"
                            onClick={() => this.handleClose("cancel")}
                            sx={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                color: (theme) => theme.palette.grey[600],
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Tooltip>
                    <Box
                        sx={{
                            padding: "10px",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            backgroundColor: "#f9f9f9",
                            marginTop: "10px",
                        }}
                    >
                        <Typography variant="body1" color="primary">
                            Name: {name || "Nill"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Email: {email || "Nill"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Mobile: {mobile || "Nill"}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent className="subMenuModal">
                    {tabValue === 0 && (
                        <TabPanel value={tabValue} index={0}>
                            <UserSettings
                                ref={this.userSettingsRef}
                                onValidationChange={this.handleValidationChange}
                                userId={userId}
                                accessToken={accessToken}
                                onClose={() => this.handleClose("cancel")}
                            />
                        </TabPanel>
                    )}
                    {this.props.isStatusHistoryVisible && tabValue === 1 && (
                        <TabPanel value={tabValue} index={1}>
                            <StatusHistory
                                userId={userId}
                                accessToken={accessToken} />
                        </TabPanel>
                    )}
                    {(
                        <TabPanel value={tabValue} index={this.props.isStatusHistoryVisible ? 2 : 1}>
                            <LoginHistory
                                userId={userId}
                                accessToken={accessToken} />
                        </TabPanel>
                    )}
                    {(this.props.isTransactionLogsVisible &&
                        <TabPanel value={tabValue} index={this.props.isStatusHistoryVisible ? 3 : 2}>
                            <TransactionLogs
                                userId={userId}
                                gridId="TransactionLogsUser"
                            />
                        </TabPanel>
                    )}
                    {(
                        <TabPanel value={tabValue} index={this.props.isStatusHistoryVisible ? 4 : 3}>
                            <UserCards
                                userId={userId}
                                accessToken={accessToken}
                            />
                        </TabPanel>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.handleClose("cancel")}>Cancel</Button>
                    {tabValue === 0 && (
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={this.handleSave}
                            disabled={hasErrors || !userDetails.isUserSettingSaveBtnVisible}
                        >
                            Save
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    }
}

export default CommonUserDialog;